import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import type { AppState } from '../state'
import type {
  AuthenticatedRequest,
  Request,
  RequestConfigInterceptor,
} from './rest.lib'

/**
 * A React hook that returns a function which executes `Request` functions by
 * automatically injecting the required `apiUrlBase` and optional `interceptor`.
 *
 * @see request In rest.lib.ts for creating `Request` functions.
 *
 * @example
 * function getUsers() {
 *   return request<{ users: User[] }>({
 *     method: 'get',
 *     url: '/users',
 *   })
 * }
 * const requestor = useRequestor()
 * const response = await requestor(getUsers())
 * console.log(response.data.users)
 *
 * @returns A function to execute `Request`s.
 */
export function useRequestor() {
  const apiUrlBase = useSelector<AppState, string>(
    (state) => state.config.apiLocation,
  )
  const performRequest = useCallback(
    <ResponseData extends object>(
      request: Request<ResponseData>,
      interceptor?: RequestConfigInterceptor,
    ) => request(apiUrlBase, interceptor),
    [apiUrlBase],
  )

  return performRequest
}

/**
 * A React hook that returns a function which executes `AuthenticatedRequest`
 * functions by automatically injecting the required `apiUrlBase`, `apiToken`,
 * and optional `interceptor`.
 *
 * @see authenticatedRequest in rest.lib.ts for creating `AuthenticatedRequest`
 *      functions.
 *
 * @example
 * function getProcedure(procedureId: IProcedureId) {
 *   return request<IProcedure>({
 *     method: 'get',
 *     url: `/procedure/${procedureId}`,
 *   })
 * }
 * const requestor = useAuthenticatedRequestor()
 * const response = await requestor(getProcedure('3220'))
 * console.log(response.data.id)
 *
 * @returns A function to execute `AuthenticatedRequest`s.
 */
export function useAuthenticatedRequestor() {
  const apiUrlBase = useSelector<AppState, string>(
    (state) => state.config.apiLocation,
  )
  const apiToken = useSelector<AppState, string>((state) => state.auth.token)
  const performRequest = useCallback(
    <ResponseData extends object>(
      request: AuthenticatedRequest<ResponseData>,
      interceptor?: RequestConfigInterceptor,
    ) => request(apiUrlBase, apiToken, interceptor),
    [apiUrlBase, apiToken],
  )

  return performRequest
}
